
import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit, Save, X, FileText, Receipt, DollarSign, ArrowDownUp, Plus } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';

interface TariffBillingSettingsProps {
  editing: boolean;
  onEditToggle: () => void;
  onSave: () => void;
}

const TariffBillingSettings: React.FC<TariffBillingSettingsProps> = ({ 
  editing, 
  onEditToggle, 
  onSave 
}) => {
  const [activeTab, setActiveTab] = useState("tariffs");
  
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Tariff & Billing</h2>
          <p className="text-muted-foreground">
            Configure tariff profiles and billing settings
          </p>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <Button variant="outline" onClick={onEditToggle} className="gap-1">
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button onClick={onSave} className="gap-1">
                <Save className="h-4 w-4" />
                Save Changes
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={onEditToggle} className="gap-1">
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-3 mb-6">
            <TabsTrigger value="tariffs" className="flex items-center gap-1">
              <Receipt className="h-4 w-4" />
              Tariff Profiles
            </TabsTrigger>
            <TabsTrigger value="billing" className="flex items-center gap-1">
              <DollarSign className="h-4 w-4" />
              Billing Rules
            </TabsTrigger>
            <TabsTrigger value="export" className="flex items-center gap-1">
              <ArrowDownUp className="h-4 w-4" />
              Import/Export
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="tariffs" className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between space-x-2">
                <div className="space-y-0.5">
                  <h3 className="font-medium text-lg">Energy Tariff Profiles</h3>
                  <p className="text-muted-foreground text-sm">
                    Configure electricity tariff structures
                  </p>
                </div>
                {editing && (
                  <Button variant="outline" size="sm" className="gap-1">
                    <Plus className="h-4 w-4" />
                    Add Profile
                  </Button>
                )}
              </div>
              
              <Separator />
              
              <div className="space-y-4">
                <div className="border rounded-md p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">Commercial TOU Profile</h4>
                        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-xs">Active</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">Time-of-Use tariff for commercial customers</p>
                    </div>
                    {editing && (
                      <Switch 
                        checked={true} 
                        disabled={!editing}
                      />
                    )}
                  </div>
                  
                  {editing && (
                    <div className="grid gap-3 mt-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="currency">Currency</Label>
                          <Select disabled={!editing} defaultValue="usd">
                            <SelectTrigger id="currency">
                              <SelectValue placeholder="Select currency" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="usd">USD ($)</SelectItem>
                              <SelectItem value="eur">EUR (€)</SelectItem>
                              <SelectItem value="gbp">GBP (£)</SelectItem>
                              <SelectItem value="jpy">JPY (¥)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="tariff-type">Tariff Type</Label>
                          <Select disabled={!editing} defaultValue="tou">
                            <SelectTrigger id="tariff-type">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="flat">Flat Rate</SelectItem>
                              <SelectItem value="tou">Time of Use</SelectItem>
                              <SelectItem value="seasonal">Seasonal</SelectItem>
                              <SelectItem value="demand">Demand Charge</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="peak-rate">Peak Rate ($/kWh)</Label>
                        <Input 
                          id="peak-rate" 
                          type="number" 
                          step="0.001"
                          defaultValue="0.145" 
                          disabled={!editing} 
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="off-peak-rate">Off-Peak Rate ($/kWh)</Label>
                        <Input 
                          id="off-peak-rate" 
                          type="number" 
                          step="0.001"
                          defaultValue="0.085" 
                          disabled={!editing} 
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {!editing && (
                  <div className="border rounded-md border-dashed p-6 flex items-center justify-center">
                    <Button variant="outline" disabled={!editing} className="gap-1">
                      <Plus className="h-4 w-4" />
                      Create New Tariff Profile
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="billing" className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between space-x-2">
                <div className="space-y-0.5">
                  <h3 className="font-medium text-lg">Billing Configuration</h3>
                  <p className="text-muted-foreground text-sm">
                    Configure billing cycles and invoicing rules
                  </p>
                </div>
              </div>
              
              <Separator />
              
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="billing-cycle">Default Billing Cycle</Label>
                  <Select disabled={!editing} defaultValue="monthly">
                    <SelectTrigger id="billing-cycle">
                      <SelectValue placeholder="Select cycle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="invoice-template">Invoice Template</Label>
                  <Select disabled={!editing} defaultValue="standard">
                    <SelectTrigger id="invoice-template">
                      <SelectValue placeholder="Select template" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard Template</SelectItem>
                      <SelectItem value="detailed">Detailed Breakdown</SelectItem>
                      <SelectItem value="simple">Simple Summary</SelectItem>
                      <SelectItem value="custom">Custom Template</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="auto-billing">Automatic Billing</Label>
                    <p className="text-sm text-muted-foreground">Generate invoices automatically at end of billing cycle</p>
                  </div>
                  <Switch 
                    id="auto-billing" 
                    checked={true} 
                    disabled={!editing}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="tax-calculation">Automatic Tax Calculation</Label>
                    <p className="text-sm text-muted-foreground">Apply appropriate tax rates based on location</p>
                  </div>
                  <Switch 
                    id="tax-calculation" 
                    checked={true} 
                    disabled={!editing}
                  />
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="export" className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between space-x-2">
                <div className="space-y-0.5">
                  <h3 className="font-medium text-lg">Import/Export Configuration</h3>
                  <p className="text-muted-foreground text-sm">
                    Configure data exchange with external billing systems
                  </p>
                </div>
              </div>
              
              <Separator />
              
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="export-format">Export Format</Label>
                  <Select disabled={!editing} defaultValue="csv">
                    <SelectTrigger id="export-format">
                      <SelectValue placeholder="Select format" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="csv">CSV</SelectItem>
                      <SelectItem value="xlsx">Excel (XLSX)</SelectItem>
                      <SelectItem value="json">JSON</SelectItem>
                      <SelectItem value="xml">XML</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="external-system">External Billing System</Label>
                  <Select disabled={!editing} defaultValue="none">
                    <SelectTrigger id="external-system">
                      <SelectValue placeholder="Select system" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="sap">SAP</SelectItem>
                      <SelectItem value="oracle">Oracle Financials</SelectItem>
                      <SelectItem value="quickbooks">QuickBooks</SelectItem>
                      <SelectItem value="custom">Custom Integration</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="auto-export">Automatic Export</Label>
                    <p className="text-sm text-muted-foreground">Export invoice data to external system automatically</p>
                  </div>
                  <Switch 
                    id="auto-export" 
                    checked={false} 
                    disabled={!editing}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    disabled={!editing}
                    className="gap-1"
                  >
                    <ArrowDownUp className="h-4 w-4" />
                    Configure Integration
                  </Button>
                  <Button 
                    variant="outline" 
                    disabled={!editing}
                    className="gap-1"
                  >
                    <FileText className="h-4 w-4" />
                    Map Data Fields
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
        
        {!editing && (
          <div className="flex justify-center items-center mt-6">
            <Button variant="default" className="gap-2">
              <FileText className="h-4 w-4" />
              View Billing Reports
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TariffBillingSettings;
